import { DOMAIN, SECURE, IS_LOCAL_DEVELOPMENT } from './cookies-config.js';
import { returnValueByEnv } from './environment.js';

/**
 * @typedef {object} LocalStorageKeys
 * @property {string} ADID - Key for storing ADID
 * @property {string} ANDROID_ID - Key for storing Android ID
 * @property {string} AOFL_TOKEN - Key for storing AOFL token
 * @property {string} APP_INSTANCE_ID - Key for storing App Instance ID
 * @property {string} DEVICE_CODE - Key for storing device code
 * @property {string} DEVICE_ID - Key for storing device ID
 * @property {string} EXPERIMENTS - Key for storing client experiments
 * @property {string} GPS_ADID - Key for storing GPS ADID
 * @property {string} IDFA - Key for storing IDFA
 * @property {string} IDFV - Key for storing IDFV
 * @property {string} SOURCE_INFO - Key for storing source information
 * @property {string} UDID - Key for storing UDID
 */
export const LOCAL_STORAGE_KEYS = {
	ADID: 'adid',
	ANDROID_ID: 'android_id',
	AOFL_TOKEN: 'aofl_token',
	APP_INSTANCE_ID: 'app_instance_id',
	DEVICE_CODE: 'device_code',
	DEVICE_ID: 'device_id',
	// The device ID is different from the above. This is added to debug the storage mechanism of safari.
	DEVICE_ID_BACKUP: 'device_id_backup',
	EXPERIMENTS: 'client-experiments',
	GPS_ADID: 'gps_adid',
	IDFA: 'idfa',
	IDFV: 'idfv',
	SOURCE_INFO: 'source_info',
	UDID: 'udid'
};

export const AOFL_PRODUCT_NAMES = {
	ABCMOUSE: 'abcmouse',
	ABCMOUSE_2_0: 'abcmouse_v2'
};

export const AOFL_LEGACY_ROLES = {
	abcmousev2_legacy_user: 'abcmousev2_legacy_user',
	abcmousev2_legacy_teacher: 'abcmousev2_legacy_teacher',
	abcmousev2_legacy_assessment: 'abcmousev2_legacy_assessment'
};

export const AOFL_2_0_ROLES = {
	abcmousev2_user: 'abcmousev2_user',
	abcmousev2_assessment_user: 'abcmousev2_assessment_user',
	abcmousev2_user_max_tier: 'abcmousev2_user_max_tier'
};

export const EXPERIMENT_ARMS = {
	ABCMOUSE_2_0: '10004',
	ABCMOUSE_1_0: '10003'
};

export const UNITY_VERSION_REQUIREMENTS = {
	MIN_VERSION_FOR_CONTINUE_TO_1_0: '8.71.0'
};

export const EVENT_NAMES = {
	MARK_EXPERIMENT_SEEN: 'ab.experiment-seen',
	CLIENT_VALIDATION: 'marketing.client_form_validation',
	EXPERIMENT_ASSIGNMENT: 'marketing.rotation_assignment',
	DEBUG: 'marketing.client_debug',
	CAMPAIGN_CLICK: 'marketing.campaign_click',
	LINK_CLICK: 'marketing.page_link_click',
	PAGE_LOAD: 'marketing.page_load',
	PIXEL_FIRE: 'marketing.pixel_fire',
	ROTATION_ASSIGN: 'marketing.rotation_assignment',
	VIDEO_PROGRESS: 'marketing.video_watched',
	PADDLE_CHECKOUT_EVENT: 'marketing.paddle_client_event',
	QR_CODE_VISITED: 'marketing.qr_code_visited',
	UI_SHOW: 'marketing.ui_show',
	ACCOUNT_SETTINGS_UPDATE: 'marketing.account_settings_update'
};

export const MEDIA_CHANNEL = {
	AFFILIATE: 'affiliate',
	FACEBOOK: 'facebook',
	GOOGLE_DISPLAY: 'google_display',
	INCENTIVIZED: 'incentivized',
	INFLUENCERS: 'influencers',
	NATIVE_APP: 'native_app',
	OTHER_NON_PERFORMANCE: 'other_non_performance',
	OTT: 'ott',
	SEARCH_BRANDED: 'search_branded',
	SEARCH_NON_BRANDED: 'search_non_branded',
	THIRD_PARTY_EMAIL: 'third_party_email',
	UNKNOWN: 'unknown'
};

export const PAYMENT_TERM = {
	ANNUAL: 'annual',
	BIENNIAL: 'biennial',
	MONTHLY: 'monthly',
	SEMI_ANNUAL: 'semi_annual'
};

export const PLATFORMS = {
	DEFAULT: 'default_platform'
};

export const ACTION_SOURCES = {
	EMAIL_SPECIAL_OFFER_UPGRADE: 'email_special_offer_upgrade',
	REG_PATH: 'regpath'
};

export const AOFL_PRODUCT_UUIDS = {
	ABCMOUSE: '2d6e4e3e-3711-499b-93e3-1919aac44a27',
	ABCMOUSE_2_0: 'b2892456-6e59-11ee-b962-0242ac120002',
	HOMESCHOOL: '4be90567-6d1a-43ab-afe6-51faa8d847eb',
	ABCMOUSE_ENGLISH_ES: 'c19722f3-9659-472e-9ccc-1aefa2c44803',
	ADVENTURE_ACADEMY: '4d984c26-1a72-11e7-93ae-92361f002671'
};

export const AOFL_USER_TYPES = {
	MIXED: 'mixed',
	CLASSIC: 'classic',
	ABCMOUSE_2_0: 'abcmouse_2_0'
};

export const GTM_EVENT_NAMES = {
	CONVERSION: 'conversion',
	PROSPECT_REG_SUBMITTED: 'prospect-reg',
	LOGIN: 'login'
};

export const PAYMENT_TYPES = {
	CREDIT_CARD: 'paymentech',
	PAYPAL: 'paypal',
	APPLE_PAY: 'apple_pay',
	MOCK_CREDIT_CARD: 'mock_cc_gateway'
};

export const ENVIRONMENTS = {
	DEVELOPMENT: 'development',
	STAGING: 'staging',
	PRODUCTION: 'production',
	LOCAL_DEVELOPMENT: 'local_development'
};

/**
 * Sentry error severity levels for use with captureException and captureMessage
 * @typedef {object} SentryErrorLevels
 * @property {string} FATAL - Highest severity level for critical errors
 * @property {string} ERROR - Standard error level (default for exceptions)
 * @property {string} WARNING - Warning level for recoverable issues
 * @property {string} LOG - Log level for informational messages
 * @property {string} INFO - Info level for general information
 * @property {string} DEBUG - Debug level for development debugging
 */
export const SENTRY_ERROR_LEVELS = {
	FATAL: 'fatal',
	ERROR: 'error',
	WARNING: 'warning',
	LOG: 'log',
	INFO: 'info',
	DEBUG: 'debug'
};

export const SUBSCRIPTION_TYPES = {
	NEW_SUBSCRIPTION: 'firstTimeSubscription',
	// covers both reactivation and buying additional products.
	EXISTING_SUBSCRIPTION: 'existingSubscription',
	ITEM_PURCHASE: 'itemPurchase'
};

export const TRANSACTION_TYPE = {
	PREPROCESS: 'preprocess',
	PROCESS: 'process'
};

export const APP_CALLS = {
	ADJUST_EVENT: 'event://adjustEvent',
	CLOSE_APP: 'application://close',
	GO_BACK: 'location://goBack?showParentGate=false',
	LOG_EVENT: 'event://logEvent',
	LOGIN: 'session://login',
	LOGOUT: 'application://logout',
	NAVIGATE: 'location://navigate',
	PURCHASE: 'transaction://purchase',
	RESTORE: 'transaction://restore',
	SESSION_GET: 'session://get',
	UPGRADE: 'transaction://upgrade'
};

export const APP_CALL_CALLBACKS = {
	ADJUST_EVENT: 'adjustCallback',
	FIREBASE_EVENT: 'firebaseCallback',
	LOGIN: 'loginCallback',
	PURCHASE: 'purchaseCallback',
	RESTORE: 'restoreCallback',
	UPGRADE: 'upgradeCallback'
};

export const THIRD_PARTY_EVENTS = {
	SUBSCRIBE: 'Subscribe',
	INITIATE_CHECKOUT: 'InitiateCheckout'
};

export const APP_STORES = {
	ITUNES: 'iTunes',
	GOOGLE_PLAY: 'GooglePlay',
	AMAZON: 'Amazon',
	APPLE_PAY_MERCHANT_ID: 'merchant.abcmouse.com'
};

export const UsernameTypes = {
	EMAIL: 'email'
};

export const OUTBOUND_LINKS = {
	get IPV6() {
		return returnValueByEnv({
			dev: 'https://deq4mt61w2hci.cloudfront.net/trk',
			production: 'https://ipv6.abcmouse.com/trk'
		});
	}
};

export const OUTBOUND_PATH = {
	LINK_TO_APP_1_0: 'https://xnvn.adj.st/?provider=adjust&adj_t=1rntjwqr&adj_label=token&token=',
	LINK_TO_APP_2_0: 'https://abcmouse.go.link/app/auth?adj_t=1kmlo5gl&adj_label=token&token=',
	LINK_TO_APP_1_0_AMAZON:
		'https://abcmouse-1-0-amz.go.link/?provider=adjust&adj_t=1td8cyt7&adj_label=token&token=',
	LINK_TO_APP_2_0_AMAZON:
		'https://abcmouse-amz.go.link/app/auth?adj_t=1r7kvsdr&adj_label=token&token='
};

export const PAGES = {
	INTERNAL: {
		HOME: '/abc',
		HOME_AOFLJS: '/abc/?arm=9100',
		HOME_ES: '/abc/es/',
		AA_OFFER: '/abc/aa-offer',
		SUBSCRIPTION: '/abc/subscription',
		SUBSCRIPTION_OFFER: (/** @type {string} */ offer) => `/abc/subscription/offer/?${offer}`,
		SUBSCRIPTION_CONSUME: '/abc/subscription-consume',
		REACTIVATION: '/abc/reactivation',
		REDEEM: '/abc/redeem',
		UPGRADE: '/abc/upgrade',
		PROSPECT_REGISTER: '/abc/prospect-register',
		DESKTOP_CHOICE: '/abc/desktop-choice',
		DOWNLOAD_APP: '/abc/download-app',
		APP_CHOICE: '/abc/app-choice',
		INSIDE_PAYWALL: '/abc/inside-pw',
		GIVE_A_GIFT: '/abc/purchase-gift/',
		LOGIN: '/abc/login',
		FORGOT_PASSWORD: '/abc/forgot-password',
		ASSESSMENT_PURCHASE: '/abc/assessment-purchase',
		INBOUND_GATEWAY: '/abc/inbound-gateway',
		LOGOUT: '/abc/logout',
		OFFER_EXPIRED: '/abc/expired',
		RESET_PASSWORD: '/abc/password-reset',
		RESET_LINK_EXPIRED: '/abc/reset-link-expired',
		RESET_PASSWORD_SUCCESS: '/abc/password-reset-success',
		PREMIUM: '/abc/premium',
		CLAIMED_OFFER: '/abc/claimed-offer',
		PARENT_SECTION: {
			DEFAULT: '/abc/parent-section',
			MEMBERSHIP: '/abc/parent-section/membership',
			BILLING: '/abc/parent-section/membership/billing',
			PAYMENT: '/abc/parent-section/membership/payment',
			ASSESSMENT_CENTER: '/abc/parent-section/assessment-center'
		},
		SUPPORT_CHATBOT: {
			DEFAULT: '/abc/support-chatbot'
		},
		CANCEL_SUBSCRIPTION: {
			DEFAULT: '/abc/cancel-subscription',
			SUCCESS: '/abc/cancel-subscription/success',
			ASSESSMENT_CENTER: '/abc/cancel-subscription/assessment-center',
			PAUSE: '/abc/cancel-subscription/pause',
			PAUSE_SUCCESS: '/abc/cancel-subscription/pause/success',
			PAUSE_MANAGE: '/abc/cancel-subscription/pause/manage',
			PAUSE_RESUMED: '/abc/cancel-subscription/pause/resumed',
			SAVE_OFFER: '/abc/cancel-subscription/save-offer',
			CONFIRMATION: '/abc/cancel-subscription/confirmation',
			CANCEL_FEEDBACK: '/abc/cancel-subscription/cancel-feedback'
		},
		LEGACY: {
			ONBOARDING_PAGE: '/html5/onboarding',
			ABOUT_US: '/about-us',
			ABOUT_US_ADVISORY_BOARD: '/about-us#advisory_board',
			COOKIE_POLICY: '/cookie-policy',
			CUSTOMER_SUPPORT: '/customer-support',
			EDUCATIONAL_GAMES: '/educational-games',
			FAQS: '/faqs',
			GIFT: '/gift',
			GIFT_CARDS: '/code',
			HOMESCHOOL: '/homeschool',
			KINDERGARTEN: '/kindergarten',
			LIBRARIES: '/libraries',
			LOGIN: '/login',
			MATH: '/math',
			PHONICS: '/phonics',
			PRE_KINDERGARTEN: '/pre-kindergarten',
			PRESCHOOL: '/preschool',
			PRIVACY_POLICY: '/privacy',
			READING: '/reading',
			TEACHERS: '/teachers',
			NEWSLETTER: '/newsletter',
			RESULTS_AND_REFERENCES: '/results-and-references',
			TANDC: '/tandc'
		},
		PRINTABLES: {
			DEFAULT: '/abc/printables'
		},
		LINKS: {
			QR_ENTRY: '/abc/qr-entry/',
			APP_CHOICE: '/abc/app-choice'
		}
	},
	EXTERNAL: {
		ABCMOUSE: 'https://www.abcmouse.com',
		get ABCMOUSE_2_0() {
			return returnValueByEnv({
				dev: 'https://dev.app.abcmouse.com/app',
				staging: 'https://stg.app.abcmouse.com/app',
				production: 'https://app.abcmouse.com/app'
			});
		},
		ABCMOUSE_1_0_APP_DEEP_LINK: 'aofl.abcmouse.com://',
		ABCMOUSE_2_0_WEB_APP_URL: 'https://app.abcmouse.com/app/',
		ABCMOUSE_2_0_APP_HOME_DEEP_LINK_: 'abcmouseapp://app.abcmouse.com/app/feature?path=/',
		ABCMOUSE_SEO_GAMES_PAGE: 'https://www.abcmouse.com/games',
		ACTIVITIES: 'https://www.abcmouse.com/learn/category-activities',
		ADVENTURE_ACADEMY: 'https://www.adventureacademy.com/',
		AOFL_ABCMOUSE_COOKIE_POLICY: 'https://www.ageoflearning.com/abc-cookie-policy/',
		AOFL_BRAND_ABCM: 'https://www.ageoflearning.com/brand/abcmouse_landing_v1',
		AOFL_FOR_SCHOOLS: 'https://www.ageoflearning.com/schools/',
		AOFL_DEVICES: 'https://www.ageoflearning.com/devices/aofl_devices_en.pdf',
		ACCESSIBILITY: 'https://www.ageoflearning.com/accessibility',
		CUSTOMER_SUPPORT: 'https://support.abcmouse.com',
		ACCESSIBILITY_STATEMENT: 'https://www.ageoflearning.com/accessibility/',
		CUSTOMER_SUPPORT_PLUS: 'https://support.plus.abcmouse.com',
		CUSTOMER_SUPPORT_PLUS_CONTACT_US: 'https://support.abcmouse.com/hc/en-us/p/contact',
		CUSTOMER_SUPPORT_CONTACT_US: 'https://support.abcmouse.com/hc/en-us/articles/360047572854',
		PRIVACY_PDF: 'https://img.abcmouse.com/pdf/privacy.pdf',
		PRIVACY_PORTAL:
			'https://privacyportal-cdn.onetrust.com/dsarwebform/a2692794-047c-4c18-b3bf-60d783ebc844/8205df50-001c-4e16-b5f9-d4e945bc612e.html',
		IPV6: 'https://ipv6.abcmouse.com/trk',
		TEACHERS: 'https://www.abcmouse.com/teachers',
		LEARN: 'https://www.abcmouse.com/learn',

		ALLABOUT_DNT: 'http://www.allaboutdnt.org',
		CCPA_METRICS: 'https://www.ageoflearning.com/ccpa/',
		CUSTOMER_SUPPORT_FORGOT_EMAIL:
			'https://support.abcmouse.com/hc/en-us/articles/4413252218903-Account-Access',
		CUSTOMER_SUPPORT_FORGOT_PASSWORD:
			'https://support.abcmouse.com/hc/en-us/articles/4413252218903?forgotpassword=true',
		CUSTOMER_SUPPORT_FORGOT_PASSWORD_PLUS:
			'https://support.plus.abcmouse.com/hc/en-us/articles/19158148938263-Account-Access',
		CUSTOMER_SUPPORT_FAQS:
			'https://support.abcmouse.com/hc/en-us/articles/360053552753-What-devices-should-I-use-with-ABCmouse-',
		AOFL_RESEARCH: 'https://www.ageoflearning.com/research/',
		GOOGLE_UCB_LEARN_MORE: 'https://support.google.com/googleplay/answer/11174377',
		HOMESCHOOLPLUS: 'https://www.homeschoolplus.com',
		HOMESCHOOLPLUS_SUBSCRIPTION: 'https://www.homeschoolplus.com/subscription/',
		SUNMAID_WALMART: 'https://www.walmart.com/cp/sunmaidbacktoschool/3335011',
		SUNMAID_SIGHT_WORDS_PDF: 'https://img.abcmouse.com/pdf/abcmouse_sunmaid_sight_words_rgb_06.pdf',
		SUNMAID_MATH_WITH_RAISINS_PDF: 'https://img.abcmouse.com/pdf/sunmaid_book_letter_12.pdf',
		SUNMAID_COLORING_PAGES_PDF: 'https://img.abcmouse.com/pdf/sunmaid_alphabet_coloring_pages.pdf',
		SUNMAID_LETTER_TRACING_PDF: 'https://img.abcmouse.com/pdf/abcmouse_letter_tracing.pdf',
		KID_SAFE: 'https://www.kidsafeseal.com/certifiedproducts/abcmouse.html',
		PRIVACY_DE: 'https://www.ageoflearning.com/abc-privacy-german',
		PRIVACY_SE: 'https://www.ageoflearning.com/abc-privacy-swedish',
		TANDC_DE: 'https://www.ageoflearning.com/abc-tandc-german',
		TANDC_SE: 'https://www.ageoflearning.com/abc-tandc-swedish',
		PRIVACY_HUB: 'https://www.ageoflearning.com/privacy-policies/',
		PRIVACY_POLICY: 'https://www.ageoflearning.com/abc-privacy-current/',
		PRINTABLES: 'https://www.abcmouse.com/learn/category-printables-worksheets',
		PRIVACY_ES: 'https://www.ageoflearning.com/abc-privacy-current-es/',
		TERMS_CONDITIONS: 'https://www.ageoflearning.com/abc-tandc-current/',
		TERMS_CONDITIONS_ES: 'https://www.ageoflearning.com/abc-tandc-current-es/',
		TIKTOK: 'https://www.tiktok.com/@abcmouse',
		FACEBOOK: 'https://www.abcmouse.com/outsideurl?url=facebook',
		FACEBOOK_PAGE: 'https://www.facebook.com/ABCmouse/',
		FACEBOOK_GROUP: 'https://www.facebook.com/groups/abcmouse.orientation/about/',
		INSTAGRAM: 'https://www.instagram.com/abcmouse/',
		PINTEREST: 'https://www.abcmouse.com/outsideurl?url=pinterest',
		PINTEREST_PAGE: 'https://www.pinterest.com/abcmouse/',
		CAREERS: 'https://www.ageoflearning.com/careers/',
		AOFL: 'https://www.ageoflearning.com',
		MEDIA: 'https://www.ageoflearning.com/media/',
		YOUTUBE: 'https://www.youtube.com/@ABCmouse',
		IOS_APP_STORE: 'https://apps.apple.com/us/app/abcmouse/id6460300848',
		GOOGLE_APP_STORE: 'https://play.google.com/store/apps/details?id=com.aofl.abcmouse',
		AMAZON_APP_STORE: 'http://www.amazon.com/gp/mas/dl/android?asin=B0F77MSX3T',
		GALAXY_APP_STORE: 'https://galaxystore.samsung.com/detail/com.aofl.abcmouse',
		CANCELLATION_SURVEY: 'https://ageoflearning.sjc1.qualtrics.com/jfe/form/SV_0x2vngZjCVRZ0BU',
		ABC_SERVICE_LAYER_BASE_URL: 'https://dev.app.abcmouse.com'
	}
};

export const QUERY_PARAM_KEYS = {
	ADID: 'adid',
	ANDROID_ID: 'android_id',
	APP_INSTANCE_ID: 'app_instance_id',
	APP_STORE_NAME: 'store',
	CJ_EVENT: 'cjevent',
	CLIENT_TYPE: 'ct',
	DEEP_LINK: 'deepLink',
	DEVICE_ID: 'device_id',
	DISTRIBUTION_PLATFORM: 'distribution_platform',
	EMAIL_CLICK: '_0c83',
	EXPIRES_TIMESTAMP: 'expires_timestamp',
	PAYLOAD: 'payload',
	PRODUCT_HASH_MONTHLY: 'productHashMonthly',
	PRODUCT_HASH_ANNUAL: 'productHashAnnual',
	GOTO: 'to',
	GPS_ADID: 'gps_adid',
	IDFA: 'idfa',
	IDFV: 'idfv',
	LINK_FROM_APP_PLATFORM: 'linkFromAppPlatform',
	PRODUCT_GROUP: 'product_group',
	PRODUCT_HASH: 'productHash',
	PRODUCT_TYPE: 'b_term',
	PRODUCT_UUID: 'product_uuid',
	REFERRER: 'referrer',
	SOURCE_TAG: 'src_tag',
	EXTRA_PATH_DATA: 'extra_path_data',
	SUBSCRIPTION_CATEGORY: 'subscription_category',
	TARGET_AOFL_PRODUCT_UUID: '_ee4c',
	SUBSCRIPTION_UUID: 'subscription_uuid',
	TOKEN: 'token',
	TV_DEVICE_CODE: 'tvdc',
	UDID: 'udid',
	UNIQUE_OFFER_EXPIRATION_TIMESTAMP: 'ts',
	USER_TOKEN: 'token',
	VIEWER_COUNTRY: 'viewerCountry',
	VIEWER_COUNTRY_REGION: 'viewerCountryRegion',
	STORE_TYPE: 'storeType',
	OPEN_SUPPORT_CHATBOT: 'open_support_chatbot',
	SUPPORT_CHATBOT_VISIBLE: 'support_chatbot_visible',
	SUPPORT_CHATBOT_FULLSCREEN: 'support_chatbot_fullscreen',
	PAUSE_FOR: 'pause_for',
	APP_VERSION: 'app_version'
};

export const QUERY_PARAM_VALUES = {
	B_TERM: {
		MONTHYL: 'monthly',
		ANNUAL: 'annual'
	}
};

export const CAMPAIGN_PIXEL_TYPES = {
	CONVERSION: 'conversion',
	LANDING: 'landing',
	PROSPECT_REGISTER: 'sign-up'
};

export const REG_PATH_KEYS = {
	DEFAULT_SUBSCRIBER: 'defaultSubscriber',
	DEFAULT_RESUBSCRIBER: 'defaultResubscriber',
	NO_AA_SUBSCRIBER: 'noAASubscriber',
	IN_APP_SUBSCRIBER: 'inAppSubscriber',
	IN_APP_RESUBSCRIBE: 'inAppResubscriber',
	GOOGLE_IN_APP_SUBSCRIBER: 'googleInAppSubscriber',
	IN_APP_UCB_SUBSCRIBER: 'inAppUcbSubscriber',
	IN_APP_UCB_RESUBSCRIBER: 'inAppUcbResubscriber',
	SUBSCRIBER_NO_PATH: 'subscriberNoPath',
	RESUBSCRIBER_NO_PATH: 'resubscriberNoPath',
	IN_APP_SAMSUNG_SUBSCRIBER: 'inAppSamsungSubscriber',
	IN_APP_SAMSUNG_RESUBSCRIBER: 'inAppSamsungResubscriber',
	DEFAULT_IN_APP_CONSUMPTION_2_0: 'defaultInAppConsumption',
	DEFAULT_IN_APP_CONSUMPTION_RESUBSCRIBE_2_0: 'defaultInAppConsumptionResubscribe',
	IOS_ALT_PAYMENT_SUBSCRIBER_2_0: 'iosAltPaymentSubscriber',
	SPECIAL_OFFER_SUBSCRIBER: 'specialOfferSubscriber'
};

export const REG_PATH_STEPS = {
	ASSESSMENT: 'REGPATH_ASSESSMENT',
	DOWNLOAD_APP: 'REGPATH_DOWNLOAD_APP',
	AA_OFFER: 'REGPATH_AA_OFFER',
	COMPLETE: 'REGPATH_COMPLETE',
	SUBSCRIBED: 'REGPATH_SUBSCRIBED', // Step at which the user has subscribed.
	UPGRADE: 'REGPATH_UPGRADE',
	RESET: 'DEFAULT_STEP' // Step to reset a reg-path: should not be included in any reg-path, used primarily for reactivation users.
};

/**
 * Marker hash used for "No thanks" option in upgrade flows.
 * This is not a real subscription hash - it's a UI convention.
 */
export const NO_THANKS_HASH = 'no_thanks_hash';

export const SUBSCRIPTION_HASHES = {
	PAYMENT_TECH: {
		aa_offer: '78a35b75-997e-44d6-8f2d-7bb6f677d164',
		monthly_fwf: '751b26740991c3895e4d2069beedfd28', // $12.99
		monthly_fwf_1499: 'cf643269fa6bf53718640211a2c582f64e996fc696cbc994df518cd2a4b89e6b', // $14.99
		monthly_fmf: 'd4vdn3idd9kr5uoexm4xij4zhhlcc9hc', // $14.99
		annual: 'a705135a5ca0263he12632d074f7796c',
		semi_annual: 'e6d014c1363cae08b8c019b7cf5d935a',
		upgrade: '6d24d3e822431efca22d73f50d4da9fb', // $45.00
		assessment_monthly: '30a5c22b794f10a376ae452bf78fb4f2', // $3.95
		assessment_annual: '2530146ebb330f58f5d633b2b23c868c',
		assessment_semi_annual: 'b0e51d17de8e6f364e414f11719f9b4c',
		gift_one_year: 'dbee304be9df12b57fd6279c76dab1eb',
		gift_two_year: '3f141af954cb78d2387f34f3be357f95',
		monthly_reactivation: 'e91fba0ff4be204eb6c6ff64d5da9057', // $14.99
		annual_reactivation: 'cb19da4514dgb2dbc5419e18s2c72b7c', // $79.99
		annual_reactivation_upgrade: 'cd44fasa2e3e754114d36bf572d4e6c2', // $59.40
		semi_annual_reactivation: '7a4852696c02f5bf2f72b93badce0f75', // $29.99
		monthly_fwf_2_0: 'ce2f536ce33f40f5890f05b924f00047b7daf7494bea8472c7c921222f5a37da', // $14.99 - 2_0
		monthly_fmf_2_0: 'f1eab9c7f585ee6e6b45e993af095abc96cbe361dc575f5539e3aaad0bc0a6a6', // $14.99 - 2_0
		monthly_fmf_2_0_upgrade_offer:
			'165fb9e5fdddb0a3eb65d2757bf88ad2f56a9804cec514670259c3327d071965', // $14.99 - 2_0 - Upgrade Offer
		monthly_no_trial_2_0: '872652378a49578c6a4d5c2d1b4eb959b7fe04cbdf0a87a30c02d184bedca8b7', // $12.99 - 2_0
		annual_2_0: '0276c52238259b3948b86616639ffaf3fa9be4eb89c976085b98835648eb9882', // $45.00 - 2_0
		annual_2_0_upgrade_offer: '54847633663053095dce1cc409214c099cb956dc1cf3672b1471e7b938de954f', // $39.99 - 2_0 - Upgrade Offer
		semi_annual_2_0: 'a671ba9535d019ed4ae92f2974fe665bd1fd9f06e08f67aadaf9ccff719e46ac', // $25.00 - 2_0
		monthly_reactivation_2_0: 'ed789ce4963b763b69a4ab495da0e2b4a4d6d7b92dd8acf4e9217e7b24ea57ea', // $14.99 - 2_0
		annual_reactivation_2_0: 'a008741e48cdcb14b845f9dbd200ddac7615c1ede49836a77d66c18570ed3c3b', // $79.99 - 2_0
		semi_annual_reactivation_2_0:
			'ceae6f76c08bf577fd3d7f35d020592e0d2168180cfc23307a743b5e6a913d17', // $29.99 - 2_0
		annual_reactivation_upgrade_2_0:
			'd2c6fd260eb98ca2c859f949f7d6901d5ad3b928426f423894e90d6d1417c643', // $59.40 - 2_0
		assessment_monthly_2_0: '8b48642b0d6244f6650daf8d6bf26776397d11ec3e76a7587f4407bff738cacc', // $3.99 - 2_0 FMF
		assessment_annual_2_0: '3f9a5f7b6ce8e1d2dbe1cdcf2f2c5b8fb206fe845e9b48daad0891fee7ea1bb5', // 39.99 - 2_0
		assessment_semi_annual_2_0: '606200ee609d5e9114c7b43f99f986deabf6d624a812fdcc36c0b4a57ea73137', // 19.99 - 2_0
		offer_annual_2_0: 'b07d926a57e60328660d47450a69ccbc6df2589a1d1396fa0fce6dcd036c720a', // 29.99 - 2_0
		offer_annual_1_0: 'c72e89a09e355cb868b9d0a7ae029790' // 29.99 - 1_0
	},
	IOS: {
		monthly_reactivation: '9e0c85b3e45f689aa3a314b095d3b5e2', // $14.99 (No. FWF)
		annual_reactivation: '8a4e4472f86b2b934b4e629bf774b39c', // $59.99
		monthly: '9e19861656864e08d9167db751086ec0', // product id: 2732 - $14.99 First Month Free
		annual: 'ad2950d952c358c62dcf9b9c001ccdee', // product id: 2736 - $45 First Year ($59.99 Annual)
		semi_annual: '0c6802bb28d2e18329776d8a316f1775', // product id: 2734 - $39.99
		upgrade: 'e09e020dde2b2460e3185d702a11b244', // product id: 2735 - $49.99
		assessment: 'c9a609d125f991492ee007cc357ff2b9' // $39.99 (Annual)
	},
	IOS_MKTABC_3691_ALT_PAYMENT_TEST: {
		monthly_reactivation: '10898e5b1eeedfd0da0ef2c10de45739', // product id: 2741 - $14.99 no trial
		annual_reactivation: 'b921a20c54bd95b00c1037b11a6d1cf7', // product id: 2746 $59.99 upfront annual
		monthly: 'e4e9d671225eeac51ff5784f4f21ce7b', // product id: 2745 - $14.99 First Month Free
		annual: 'a63a594898fca13efc89ee7640597aca', // product id: 2744 - $45 First Year ($59.99 Annual)
		semi_annual: '32ea168aecaf02dd223af15a78fa5b7d', // product id: 1742 - $39.99
		upgrade: 'ff89a9fe4e13691998003024dc00f7b9', // product id: 2743 - $49.99
		assessment: 'aa047073b571692520c230d25ecf9a09' // product id: 2748 $39.99 (Annual)
	},
	AMAZON: {
		monthly_reactivation: 'cbe475080673df42c6bdfb995b46931e', // $12.99
		annual_reactivation: 'a1430b7a89569a3d3270160cb882d995', // $59.99
		annual: '7d8a6b9c4e3f1b5d2c7e0f1a8b2d6c9f', // $49.99
		semi_annual: '3c51e00d5db8dabba4bd3f7a5d45e6cd', // $29.99
		monthly: 'bdc8247b33e3369a502e2ec85fc46312' // $14.99
	},
	ANDROID: {
		annual_iap: 'c59d36a81c3f20ff504400d872b93036', // $45 annual
		annual_resub_iap: 'dbca284350428313df5993a53a69dbfb', // $45 reactivation
		monthly_iap: '00e50f0b3505b45d5713f4498f279c1bc728d1775eba802264b44f0f6cd0db9f', // 1710 - $14.99
		monthly_no_trial_resub_iap: '75ad7b06a31b9ac4733af4ddce331da6', // $12.99 reactivation
		annual_ucb: '2e695f6b5a244b24fc2d5bb99981416c', // $45 annual
		annual_resub_ucb: '2e695f6b5a244b24fc2d5bb99981416c', // $45 reactivation
		annual_resub_ucb_gb: '1cabcdd2cbda6bb07c852053ca65e050916617dbf112dd86f3c54c678836206d', // 1827 - $45 annual
		monthly_ucb: 'f4a0a099513e52fd17d5b3e97518f30356004eda68d537aa0faa165fb7eb38d4', // 1712 - $14.99
		monthly_no_trial_resub_ucb: 'b3adcdb80453082703928d7f6b429a11', // $12.99 reactivation
		annual_upgrade_ucb: 'bd6eaf157d9983d60dd704dedcebe709', // $45 annual
		annual_ucb_gb: '3762ee0a70d541717d098f89a239e25de45e5eb19a5c1475d9c65966936dc105', // 1827 - $45 annual
		monthly_ucb_gb: '411a269086d94ce3646b6ac656018946a6bd7e308db1f929727b7ef718b4862b', // 1821 - $14.99
		monthly_no_trial_resub_ucb_gb:
			'92871caf0af5d09191b46d6b220ade4078c82a7676cfea52dfe11cff3d43d27f', // 1828 - $12.99 reactivation
		semi_annual_upgrade_ucb: '6c1dd228cea87c7ea5c3092da5064be7' // $25.00
	}
};

export const AUXILIARY_PRODUCT_TO_ROLE = {
	assessment: 'abcmouse_assessment_user'
};

export const COOKIES = {
	CONFIG: {
		secure: SECURE,
		domain: DOMAIN,
		expires: 365
	},
	CJ_CONFIG: {
		secure: SECURE,
		domain: DOMAIN,
		expires: 35
	},
	DEVICE_ID: 'device_id',
	get DEVICE_ID_CONFIG() {
		return {
			secure: SECURE,
			expires: 399, // days
			path: '/',
			domain: IS_LOCAL_DEVELOPMENT ? '' : '.abcmouse.com',
			sameSite: /** @type {'lax'} */ ('lax')
		};
	},
	get DEVICE_ID_SSR_CONFIG() {
		// SSR config inherits from DEVICE_ID_CONFIG but excludes 'expires' (js-cookie format)
		// and uses 'maxAge' (SvelteKit format) instead
		const { expires, ...baseConfig } = this.DEVICE_ID_CONFIG;
		return {
			...baseConfig,
			secure: !IS_LOCAL_DEVELOPMENT,
			maxAge: expires * 24 * 60 * 60, // Convert days to seconds
			httpOnly: false
		};
	},
	CJ_COOKIE: 'cje',
	TRACK_COOKIE: 'track',
	ASSESSMENT_LOGIN: 'assessmentLogin',
	GDPR: 'gdpr',
	EXPERIMENTS: 'x-aofl-experiment-info',
	LEGACY_CAMPAIGN_ID: 's_cid',
	LEGACY_CAMPAIGN_ID_TWO: 's_cid2',
	LEGACY_CAMPAIGN_ID_THREE: 'original_s_cid', // It's stupid, I know (AD). It's okay; at least it's not called dim_52, progress (PG). Perpetual migration (AD).
	LEGACY_CAMPAIGN_VARS: 's_cid_vars',
	UDID: 'udid',
	ADID: 'adid',
	FBC: '_fbc',
	FBP: '_fbp',
	REFERRER: 'referrer',
	RATE_LIMIT_COOKIE: 'abc_forgot_password_rate_limit_until'
};

export const COOKIES_CONSENT = {
	ANALYTICS: 'ANALYTICS_PERFORMANCE_COOKIES',
	TARGETING: 'TARGETING_COOKIES'
};

// TODO: merge this, no reason to have a separate list.
/** @type {{[key:string]: string; }} */
export const COUNTRY_CODES_GDPR = {
	AT: 'Austria',
	AU: 'Australia',
	BE: 'Belgium',
	BG: 'Bulgaria',
	CY: 'Republic of Cyprus',
	CZ: 'Czech Republic',
	DE: 'Germany',
	DK: 'Denmark',
	EE: 'Estonia',
	ES: 'Spain',
	EU: 'European Union',
	FI: 'Finland',
	FR: 'France',
	GB: 'United Kingdom',
	GR: 'Greece',
	HU: 'Hungary',
	HR: 'Croatia',
	IE: 'Ireland',
	IT: 'Italy',
	LT: 'Lithuania',
	LU: 'Luxembourg',
	LV: 'Latvia',
	MT: 'Malta',
	NL: 'Netherlands',
	PL: 'Poland',
	PT: 'Portugal',
	RO: 'Romania',
	SE: 'Sweden',
	SK: 'Slovakia',
	SI: 'Slovenia',
	GDPR: 'GDPR'
};

export const CURRENCY_CODE = {
	AUSTRALIAN_DOLLAR: 'AUD',
	CANADIAN_DOLLAR: 'CAD',
	CHINESE_YUAN: 'CNY',
	EURO: 'EUR',
	BRITISH_POUND: 'GBP',
	ISRAELI_SHEKEL: 'ILS',
	JAPANESE_YEN: 'JPY',
	MEXICAN_PESO: 'MXN',
	NEW_ZEALAND_DOLLAR: 'NZD',
	SWEDISH_KRONA: 'SEK',
	SINGAPORE_DOLLAR: 'SGD',
	TAIWAN_DOLLAR: 'TWD',
	US_DOLLAR: 'USD',
	VIETNAMESE_DONG: 'VND'
};

export const CURRENCY_SYMBOL = {
	AUD: '$',
	CAD: '$',
	CNY: '¥',
	EUR: '€',
	GBP: '£',
	ILS: '₪',
	JPY: '¥',
	MXN: '$',
	NZD: '$',
	SEK: 'kr',
	SGD: '$',
	TWD: 'NT$',
	USD: '$',
	VND: '₫'
};

/**
 * @enum {string}
 */
export const LINK_FROM_APP_PLATFORMS = {
	DEFAULT: 'none',
	IOS: 'ios',
	ANDROID: 'android'
};

export const DESKTOP_CHOICE = {
	ABCMOUSE_CLASSIC: 'abcmouse-1',
	ABCMOUSE_2_0: 'abcmouse-2'
};

export const IOS_ALT_PAYMENT_EXPERIMENT_ID_TEST = '10048';
